import React, { Suspense, useState, useEffect, useMemo } from 'react';
import {
  Container,
  CircularProgress,
  Grid,
  Box,
  Typography,
} from '@mui/material';
import { useGetBuildingsQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useGetAccountsQuery } from '../../../api/recalc/endpoints/accountsApiSlice';
import { useGetSubscriptionsQuery } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import FilterPanel from './FilterPanel';
import enrichAccounts from '../../../utils/enrichAccounts';
import { useNavigate, useLocation } from 'react-router-dom';
import accountGroups from '../../../utils/accountGroups.json';

import OperationsCard from '@/features/building-optimization-operations-dashboard/components/OperationsCard';

const groupAccountsById = (accounts, groups) => {
  const grouped = {};

  // Initialize groups with empty arrays
  for (const group in groups) {
    if (groups.hasOwnProperty(group)) {
      grouped[group] = [];
    }
  }

  // Add an "Others" group for ungrouped accounts
  grouped['Others'] = [];

  // Assign accounts to groups based on their IDs
  accounts?.forEach((account) => {
    let assigned = false;
    for (const [group, ids] of Object.entries(groups)) {
      if (ids.includes(account.AccountId)) {
        grouped[group].push(account);
        assigned = true;
        break;
      }
    }
    // If the account doesn't match any group, assign it to "Others"
    if (!assigned) {
      grouped['Others'].push(account);
    }
  });

  return grouped;
};

const OperationsPage = () => {
  const [enrichedAccounts, setEnrichedAccounts] = useState([]);
  const [activeFilter, setActiveFilter] = useState(true);
  const { state } = useLocation();

  const accountGroup = state?.accountGroup || {}; // Default to {} if state is undefined

  const navigate = useNavigate();

  useEffect(() => {
    if (!accountGroup) {
      navigate('/operations', { replace: true });
    }
  }, [accountGroup, navigate]);

  const handleCardDoubleClick = (groupKey, accountGroup) => {
    const encodedGroupKey = encodeURIComponent(groupKey);
    navigate(`/operations/${encodedGroupKey}`, {
      state: { accountGroup, activeFilter },
    });
  };

  const { data: buildings, isLoading: isLoadingBuildings } =
    useGetBuildingsQuery({
      pollingInterval: 3000,
      skipPollingIfUnfocused: true,
    });
  const { data: accounts, isLoading: isLoadingAccounts } = useGetAccountsQuery({
    pollingInterval: 3000,
    skipPollingIfUnfocused: true,
  });
  const { data: subscriptions, isLoading: isLoadingSubscriptions } =
    useGetSubscriptionsQuery({
      pollingInterval: 3000,
      skipPollingIfUnfocused: true,
    });

  // Determine the current loading state
  const loadingMessage = useMemo(() => {
    if (isLoadingAccounts) return 'Loading accounts...';
    if (isLoadingBuildings) return 'Loading buildings...';
    if (isLoadingSubscriptions) return 'Loading subscriptions...';
    return null;
  }, [isLoadingAccounts, isLoadingBuildings, isLoadingSubscriptions]);

  // Memoize enriched accounts
  const enrichedAccountsMemo = useMemo(() => {
    if (!loadingMessage) {
      return enrichAccounts(accounts, buildings, subscriptions);
    }
    return [];
  }, [loadingMessage, accounts, buildings, subscriptions]);

  useEffect(() => {
    if (!loadingMessage) {
      setEnrichedAccounts(enrichedAccountsMemo);
    }
  }, [loadingMessage, enrichedAccountsMemo]);

  const groupedAccounts = useMemo(
    () => groupAccountsById(enrichedAccounts, accountGroups),
    [enrichedAccounts],
  );

  const filteredAccountGroups = useMemo(() => {
    return Object.entries(groupedAccounts).reduce(
      (result, [groupKey, accounts]) => {
        const filteredAccounts = accounts
          .map((account) => {
            const filteredBuildings = account.Buildings.filter((building) =>
              building.JobSubscriptions.some(
                (sub) => !activeFilter || sub.StateCode === 'Active',
              ),
            );
            return { ...account, Buildings: filteredBuildings };
          })
          .filter((account) => account.Buildings.length > 0);

        if (filteredAccounts.length > 0) {
          result[groupKey] = filteredAccounts;
        }
        return result;
      },
      {},
    );
  }, [groupedAccounts, activeFilter]);

  return (
    <>
      {' '}
      {/* Adjust margin top to create space below AppBar */}
      {/* <FilterPanel
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
      /> */}
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 4 }}>
              Getting things ready...
            </Typography>
          </Box>
        }
      >
        {loadingMessage ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '80vh',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 4 }}>
              {loadingMessage}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {Object.entries(filteredAccountGroups)?.map(
              ([groupKey, accounts]) => (
                <Grid item sm={6} md={6} lg={4} xl={3} key={groupKey}>
                  <OperationsCard
                    groupKey={groupKey}
                    accounts={accounts}
                    onCardDoubleClick={() =>
                      handleCardDoubleClick(groupKey, accounts)
                    }
                    activeFilter={activeFilter}
                  />
                </Grid>
              ),
            )}
          </Grid>
        )}
      </Suspense>
    </>
  );
};

export default OperationsPage;
